/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  HStack,
  ScaleFade,
  Skeleton,
  Stack,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import React from 'react';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import CQLineChart from '../../../../components/CQLineChart';
import AppColors from '../../../../constants/AppColors';
import { CUSTOM, XAXISLABEL } from '../../../../constants/constants';
import { TriangleIcon, TriangleRedIcon } from '../../../../constants/IconData';
import { FONT_FAMILY } from '../../../../constants/Theme';
import { PhysicianContext } from '../../../../providers/PhysicianProvider';
import physicianRepository from '../../../../repositories/PhysiciansRepository';
import {
  convertToPositiveDollar,
  dateFormat,
  toTitleCase,
} from '../../../../utils/common.utils';
import { getChartData } from '../../getChartData';
import { utcToZonedTime } from 'date-fns-tz';
import InfoTooltip from '../../../../components/InfoTooltip';

function PTotalUploadsChart({
  defaultStartDate,
  defaultEndDate,
  startDate,
  endDate,
  filter,
  selectedDates,
}) {
  const { physicianId } = useParams();
  const [isFetching, ifState] = useBoolean(false);
  const [graphData, setGraphData] = React.useState(null);
  const physicianContext = useContext(PhysicianContext);
  const { showComparison } = physicianContext;

  const setCustomDates = filterParams => {
    for (const prop in selectedDates) {
      filterParams[prop] = dateFormat(
        new Date(selectedDates[prop]),
        'yyyy-MM-dd'
      );
    }
  };

  const fetchTotalUploads = async () => {
    try {
      ifState.on();
      const filterParams = {};
      filterParams.filter = filter.toLowerCase();

      if (filter === CUSTOM.toLowerCase()) {
        setCustomDates(filterParams);
        // setFilters(
        //   filterParams,
        //   dateFormat(defaultStartDate, 'yyyy-MM-dd'),
        //   dateFormat(defaultEndDate, 'yyyy-MM-dd'),
        //   dateFormat(startDate, 'yyyy-MM-dd'),
        //   dateFormat(endDate, 'yyyy-MM-dd')
        // );
        delete filterParams.filter;
      }
      filterParams.provider_id = physicianId;

      const response = await physicianRepository.getTotalUploadsData(
        filterParams
      );

      setGraphData(response);
      return response;
    } catch (error) {
      setGraphData(null);
    } finally {
      ifState.off();
    }
  };

  React.useEffect(() => {
    fetchTotalUploads();
  }, [defaultStartDate, defaultEndDate, startDate, endDate, filter]);

  const renderTotalUploads = () => {
    const { current_graph: graph, previous_graph, unit_type, filter_dates } = graphData;
    let chartData = { label: [], data: [] };
    let previousChartData = { label: [], data: [] };
    let legendDate = graph && Object.keys(graph);
    let label = filter;
    const chartLabel =
      label === CUSTOM.toLowerCase() ? 'custom' : label;
    if (graph) {
      chartData = getChartData(
        chartLabel,
        graph,
        unit_type,
        previous_graph,
        showComparison
      );
      previousChartData = getChartData(
        chartLabel,
        previous_graph,
        unit_type,
        [],
        showComparison
      );
    }

    const startDateUTC = utcToZonedTime(
      new Date(filter_dates ? new Date(filter_dates['start_date']) : new Date())
    );

    const endDateUTC = utcToZonedTime(
      new Date(filter_dates ? new Date(filter_dates['end_date']) : new Date())
    );

    let chartDataProp = [
      {
        label: `${format(new Date(startDateUTC), 'MMM dd, yyyy')} - ${format(
          new Date(endDateUTC),
          'MMM dd, yyyy'
        )} (${unit_type? "In "+unit_type : "No Data"})`,
        data: chartData.data,
        borderColor: AppColors.primary,
        backgroundColor: AppColors.primary,
        tension: 0.4,
      },
    ];

    if (filter.toLowerCase() !== 'all' && showComparison) {
      chartDataProp = [
        {
          label: `${format(new Date(startDateUTC), 'MMM dd, yyyy')} - ${format(
            new Date(endDateUTC),
            'MMM dd, yyyy'
          )} (${unit_type? "In "+unit_type : "No Data"})`,
          data: chartData.data,
          borderColor: AppColors.primary,
          backgroundColor: AppColors.primary,
          tension: 0.4,
        },
        {
          label: `${format(
            new Date(filter_dates.previous_start_date),
            'MMM dd, yyyy'
          )} - ${format(
            new Date(filter_dates.previous_end_date),
            'MMM dd, yyyy'
          )} (${unit_type? "In "+unit_type : "No Data"})`,
          data: previousChartData?.data,
          borderColor: AppColors.secondary,
          backgroundColor: AppColors.secondary,
          tension: 0.4,
        },
      ];
    }
    return (
      <Box mt={4}>
        <CQLineChart
          chartData={chartDataProp}
          yLabel="# of Uploads"
          xLabel={XAXISLABEL[chartLabel]}
          xAxis={chartData.label}
          x2Axis={chartData.comparisonLabel}
          showComparison={showComparison}
          maxTicksLimit={7}
          optionProps={{
            plugins: {
              legend: {
                onClick: () => { },
                // display: filterParams.label === 'custom' ? true : false,
                display: true,
                position: 'bottom',
                align: 'end',
                labels: {
                  boxWidth: 20,
                  boxHeight: 0.5,
                  color: 'black',
                  font: {
                    size: 10,
                    family: FONT_FAMILY.ptSans,
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: context => {
                    return context.parsed.y
                  },
                  title: data => {
                    const context = data[0]
                    if (context.datasetIndex === 1 && chartData.comparisonLabel.length) {
                      return chartData.comparisonLabel[context.parsed.x]
                    }
                    return context.label
                  }
                },
              },
            },
          }}
        />
      </Box>
    );
  };

  const renderTotalUploadsContainer = () => {
    return (
      <ScaleFade initialScale={0.5} in={true}>
        <Box
          bg={AppColors.white}
          borderRadius={'md'}
          padding={4}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
        >
          {/* header */}
          <HStack justifyContent={'space-between'} gap={'50px'}>
            <Text
              fontSize="sm"
              fontFamily={FONT_FAMILY.ptSans}
              color={AppColors.primary}
            >
              Total Charts
            </Text>
            <InfoTooltip infoContent={
              <>
                <p>
                  <b>Total Charts</b> component provides a visual representation of the number of charts within the selected filter date range.
                </p>
              </>} />
          </HStack>
          {/* content */}

          {renderTotalUploads()}
          {/* footer */}
          <Box mb={2} ml={10}>
            <HStack>
              {graphData?.upload_difference > 0 ? (
                <TriangleIcon />
              ) : (
                <TriangleRedIcon />
              )}
              <Text
                fontSize="sm"
                fontFamily={FONT_FAMILY.ptSans}
                fontWeight={600}
              >
                {convertToPositiveDollar(graphData?.upload_difference) || 0}
              </Text>
            </HStack>

            <Text
              fontFamily={FONT_FAMILY.ptSans}
              fontSize={'3xl'}
              fontWeight={'semibold'}
            >
              {graphData?.current_total_uploads_count || 0}
            </Text>
          </Box>
        </Box>
      </ScaleFade>
    );
  };

  const renderFailedPage = () => {
    return (
      <VStack justifyContent={'center'} h={'100%'} textAlign={'center'}>
        <Text>Something went wrong, Please try again later</Text>
      </VStack>
    );
  };

  const renderLoader = () => {
    return (
      <Stack p={4}>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  };

  const renderMainContent = () => {
    if (isFetching) {
      return renderLoader();
    } else if (!graphData) {
      return renderFailedPage();
    } else {
      return renderTotalUploadsContainer();
    }
  };

  return renderMainContent();
}

export default PTotalUploadsChart;